import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { strings, getCurrentLocale } from 'APP_ROOT/localization';
import MyQueryRenderer from "../MyQueryRenderer";
import moment from 'moment';
import ReactTable from 'react-table'; /* for the report tables */
import { csvFormat } from 'd3-dsv';
import 'react-table/react-table.css';

import ErrorMessage from 'APP_ROOT/components/ErrorMessage';
import RavenUtil from 'APP_ROOT/components/common/ravenUtil';

import MaintenanceMessage from '../MaintenanceMessage';

function toHHMMSS(sec_num) {

    var hours   = Math.floor(sec_num / 3600);
    var minutes = sec_num % 3600;
    var seconds = Math.floor(minutes % 60);
    minutes = Math.floor(minutes / 60);

    if (hours   < 10) {hours   = "0"+hours;}
    if (minutes < 10) {minutes = "0"+minutes;}
    if (seconds < 10) {seconds = "0"+seconds;}

    if(hours === 0)
        return minutes+':'+seconds;
    else
        return hours+':'+minutes+':'+seconds;
}

class ReportsSection extends React.PureComponent {

    render() {

        var myLastQuery = {
                    "timezone": Intl.DateTimeFormat().resolvedOptions().timeZone,
                    "timeDimensions": [
                        {
                            "dimension": "LatestRavenSegment.endTime",
                            "dateRange": [
                                '2018-01-01', // Hardcode the start time, only use end time
                                moment().format('YYYY-MM-DD'), // Today
                            ]
                        }
                    ],
                    "measures": [
                        "LatestRavenSegment.odometerInKilometers",
                    ],
                    "dimensions": [
                        "LatestRavenSegment.endTime",
                        "LatestRavenSegment.type",
                        "LatestRavenEvent.timestamp",
                        "DimEventType.name",
                        "DimRaven.uuid",
                        "DimRaven.enclosureSerialNo",
                        "DimRaven.imei",
                        "DimRaven.iccid",
                        "DimRaven.ravenExternalId",
                        "DimRaven.activationTime",
                        "DimVehicle.name",
                        "DimVehicle.vin",
                        "DimVehicle.make",
                        "DimVehicle.model",
                        "DimVehicle.year",
                    ],
                    "order": {
                        "DimRaven.enclosureSerialNo": 'asc',
                    },
                };

        return (
            <MyQueryRenderer
                user={this.props.user}
                cubejsApi={this.props.cubejsApi}
                query={myLastQuery}
                render={this.renderChart(myLastQuery, ConnectedReportTable, this.props)}
            />
        );
    }

    renderChart = (myLastQuery, Component, props) => ({ resultSet, error, loadingState, ...otherProps }) => {
        if (error) {
            return this.renderError(error);
        } else if(resultSet) {

            return (
                <Component
                    {...otherProps}
                    stage={props.user.stage}
                    groupBy={props.groupBy}
                    unitOfMeasure={props.unitOfMeasure}
                    resultSet={resultSet}
                    loadingState={loadingState}
                    features={props.features}
                    limitTo={props.limitTo}
                    limitToFilteredResources={props.limitToFilteredResources}
                />
            );
        } else {
            return <div>{strings.loading}</div>;
        }
    };

    renderError = error => {
        if(error.message && error.message.match(/maintenance/i)) {
            return <MaintenanceMessage message={error.message} />;
        } else {
            return <ErrorMessage error={error} />;
        }
    }
}

// A HOC that wraps a value in a link to /en/stats/history
const HistoryLinkCell = cardIdFieldName => ({ value, original }) => (
    <a
        href={original._historyUrlOverride || `/${getCurrentLocale()}/stats/history/${original['DimRaven.uuid']}/${original[cardIdFieldName]}`}
        target='_blank'
        rel='noopener noreferrer'
    >
        {value}
    </a>
);

// A HOC that places a timestamp value into the provided WrapperComponent
const TimeCell = WrapperComponent => ({value, ...rest}) => (
    <WrapperComponent
        {...rest}
        value={moment.utc(value).local().format("hh:mm:ss a")}
    />
);

class ReportTable extends React.PureComponent {

    getDimensionColumn = (name, field, overrides = {}) => {
        return ({
            Header: name,
            accessor: d => d[field],
            id: field,
            ...overrides
        });
    };

    generateExportURI = (columns, rawRows) => {
        const outputRows = rawRows.map(row => {
            const outRow = {};
            columns.forEach(col => {
                if((col.show || col.show === undefined || col.export)
                    && (col.export || col.export === undefined)) {
                    outRow[col.Header] = (typeof col.accessor === 'string' ? row[col.accessor] : col.accessor(row));
                }
            });
            return outRow;
        });

        const BOM='\uFEFF';
        return `data:text/csv;charset=utf-8,${BOM}${encodeURIComponent(csvFormat(outputRows))}`;
    }

    render() {
        const { resultSet, stage } = this.props;

        var dims = [];
        var sort = [];

        var unitsString = '(mi)';

        if(this.props.unitOfMeasure === 'METRIC')
        {   
            unitsString = '(km)';
        }

        dims.push( {
                Header: strings.reports_header_vehicle_state,
                Cell: d => {
                    if(d.value === 'unplugged')
                        return (<img src="/images/icon-unplugged_h144.svg" className="icon-trip-type unplugged"></img>);
                    else if(d.value === "driving")
                        return (<img src="/images/icon-driving_default-offset_h144-black.svg" className="icon-trip-type driving"></img>);
                    else
                        return (<img src="/images/icon-parked_h144.png" className="icon-trip-type parked"></img>);
                    },
                accessor: d => {
                    // If the most recent event is related to it being unplugged, report that
                    if(d['DimEventType.name'] === 'EVENT_RAVEN_MQTT_DISCONNECTED' ||
                        d['DimEventType.name'] === 'EVENT_RAVEN_UNPLUGGED' ||
                        d['DimEventType.name'] === 'EVENT_RAVEN_SHUTDOWN' ||
                        d['DimEventType.name'] === 'EVENT_RAVEN_USER_SHUTDOWN')
                    {
                        return 'unplugged';
                    }
                    else if(d['LatestRavenSegment.type'] === "driving")
                        // Swap driving/parked because we don't have the latest segment, only the second last one
                        return 'parked';
                    else
                        return 'driving';
                    },
                id: 'LatestRavenSegment.type',
                maxWidth: 50
            });

        dims.push(this.getDimensionColumn(strings.reports_header_raven,'DimRaven.enclosureSerialNo'));
        sort.push({id:'DimRaven.enclosureSerialNo', desc: false});

        dims.push(this.getDimensionColumn(strings.reports_header_name,'DimVehicle.name'));

        dims.push(this.getDimensionColumn(strings.reports_header_vehicle,'DimVehicle.make', { Cell: x => { if (x.value) return ( <div title={x.original["DimVehicle.vin"]}> { "" + x.original["DimVehicle.year"] + " " + x.value + " " + x.original['DimVehicle.model'] } </div> ); return (<div>{strings.reports_vehicle_name_unknown}</div>); } } ));

        dims.push(this.getDimensionColumn(strings.reports_header_vin,'DimVehicle.vin'));

        dims.push(this.getDimensionColumn(strings.reports_header_odometer + " " + unitsString ,'LatestRavenSegment.odometerInKilometers', { 
                Cell: d => d && d.value > 0 ? d.value.toFixed(0) : '-',
                accessor: d => d ? RavenUtil.round(RavenUtil.convertDistance(d['LatestRavenSegment.odometerInKilometers'], this.props.unitOfMeasure), 0) : 0
        }));


        const columns = dims.concat([
            {
                Header: strings.reports_header_vehicle_last_state_change,
                accessor: d => d['LatestRavenSegment.endTime'],
                id: 'LatestRavenSegment.endTime',
                Cell: x => <div> { x.value ? moment.utc(x.value).local().format("MMM Do YYYY") : "-" } </div>,
            },
            {
                Header: strings.reports_header_first_provisioned,
                accessor: d => d['DimRaven.activationTime'],
                id: 'DimRaven.activationTime',
                Cell: x => <div> { x.value ? moment.utc(x.value).local().format("MMM Do YYYY") : "-" } </div>,
            },

        ]);

        columns.push(this.getDimensionColumn(strings.reports_header_imei,'DimRaven.imei'));
        columns.push(this.getDimensionColumn(strings.reports_header_iccid,'DimRaven.iccid'));
        if(this.props.features.show_raven_external_id)
        {
            columns.push(this.getDimensionColumn(strings.reports_header_subscriber_id,'DimRaven.ravenExternalId'));
        }

        const rows = this.props.limitToFilteredResources(resultSet.rawData(), this.props.limitTo);

        if(!rows || rows.length <= 0)
        {
            return (
                <div>
                    <h4>{strings.reports_no_data_for_selected_time_period}</h4>
                </div>
            );
        }

        const exportURI = this.generateExportURI(columns, rows);

        return (
            <div>
                <ReactTable
                    data = { rows }
                    columns = {columns}
                    noDataText={strings.reports_no_data_for_selected_time_period}
                    showPagination = {false}
                    defaultPageSize = {Number.MAX_SAFE_INTEGER} // RAV-1942, step one (effectively "set the page size to the number of rows" returned from the database)
                    minRows = {0} /* 0, do not show empty filler rows */
                    defaultSorted={[
                        ...sort,
                    ]}
                    getTrProps={(state, rowInfo, column) => {
                        if(rowInfo && rowInfo.aggregated)
                            return { className: '-rollup' };
                        return { className: '-padRow'};
                    }}
                />
                <div className='text-right mt-1'>
                    <a
                        className='btn btn-outline-blue btn-sm'
                        download="raven-report.csv"
                        id="export-link"
                        href={exportURI}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        {strings.action_export_CSV}
                    </a>
                </div>
            </div>
        );
    }
}

const ConnectedReportTable = connect()(ReportTable);

ReportsSection.propTypes = {
    selectedDateRange: PropTypes.instanceOf(Array).isRequired,
}

export default ReportsSection;
